import { type ReactNode, useState, useCallback } from 'react';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';

const WALLET_RE = /^0x[a-fA-F0-9]{40}$/;
const ERR_INVALID =
  'Dirección inválida. Debe empezar por 0x y tener 42 caracteres.';
const ERR_SAVE = 'Error al guardar. Inténtalo de nuevo.';

type FormState = {
  value: string;
  error: string;
  saving: boolean;
  saved: boolean;
};
type SetState = React.Dispatch<React.SetStateAction<FormState>>;

async function saveWallet(
  addr: string,
  setS: SetState,
  refresh: () => Promise<unknown>
): Promise<void> {
  if (!WALLET_RE.test(addr)) {
    setS((p) => ({ ...p, error: ERR_INVALID }));
    return;
  }
  setS((p) => ({ ...p, saving: true, error: '' }));
  try {
    await pb.send('/api/team/me/wallet', {
      method: 'POST',
      body: { usdc_wallet: addr },
    });
    setS((p) => ({ ...p, saving: false, saved: true }));
    await refresh();
  } catch {
    setS((p) => ({ ...p, saving: false, error: ERR_SAVE }));
  }
}

function useWalletSave(initial: string) {
  const refreshAuth = useAuthStore((s) => s.refreshAuth);
  const [s, setS] = useState<FormState>({
    value: initial,
    error: '',
    saving: false,
    saved: false,
  });
  const setValue = useCallback(
    (v: string) => setS((p) => ({ ...p, value: v, saved: false, error: '' })),
    []
  );
  const submit = useCallback(
    () => saveWallet(s.value.trim(), setS, refreshAuth),
    [s.value, refreshAuth]
  );
  return { ...s, setValue, submit };
}

const INPUT_CLS =
  'w-full rounded-lg border border-border-subtle bg-transparent px-sm py-sm text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-red focus:outline-none';
const BTN_CLS =
  'w-full rounded-lg bg-brand-red px-base py-sm text-sm font-medium text-white transition-colors hover:bg-brand-coral disabled:opacity-50';

export function WalletForm({ current }: { current: string }): ReactNode {
  const { value, error, saving, saved, setValue, submit } =
    useWalletSave(current);

  return (
    <div className="space-y-sm">
      <label className="block text-sm text-text-secondary">
        Dirección Ethereum (USDC)
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="0x..."
        className={INPUT_CLS}
      />
      {error && <p className="text-xs text-error">{error}</p>}
      {saved && <p className="text-xs text-success">Guardada correctamente</p>}
      <button onClick={submit} disabled={saving} className={BTN_CLS}>
        {saving ? 'Guardando...' : 'Guardar'}
      </button>
    </div>
  );
}
